// components/ui/Tooltip.tsx
import React, { useState, useRef, useEffect } from 'react';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left' | 'auto';

interface TooltipProps {
  content: React.ReactNode;
  position?: TooltipPosition;
  children: React.ReactElement;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  position = 'auto', 
  children,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [calculatedPosition, setCalculatedPosition] = useState<string>('bottom-full left-1/2 transform -translate-x-1/2 mb-1');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Calculate optimal position for the tooltip when it becomes visible
  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Default position (top center)
      let newPosition = 'bottom-full left-1/2 transform -translate-x-1/2 mb-1';

      // If position is specified and not auto, use that position
      if (position !== 'auto') {
        switch (position) {
          case 'top':
            newPosition = 'bottom-full left-1/2 transform -translate-x-1/2 mb-1';
            break;
          case 'right':
            newPosition = 'left-full top-1/2 transform -translate-y-1/2 ml-1';
            break;
          case 'bottom':
            newPosition = 'top-full left-1/2 transform -translate-x-1/2 mt-1';
            break;
          case 'left':
            newPosition = 'right-full top-1/2 transform -translate-y-1/2 mr-1';
            break;
        }
      } else {
        // Auto-positioning logic
        // Check right edge overflow
        if (tooltipRect.right > viewportWidth) {
          // If tooltip extends beyond right edge, try right-aligned position
          newPosition = 'bottom-full right-0 mb-1';
        }
        
        // Check left edge overflow
        if (tooltipRect.left < 0) {
          // If tooltip extends beyond left edge, try left-aligned position
          newPosition = 'bottom-full left-0 mb-1';
        }

        // For tooltips near the screen edge, ensure they stay within viewport
        if (triggerRect.right + 100 > viewportWidth) {
          // If near right edge of screen
          newPosition = 'bottom-full right-0 mb-1';
        } else if (triggerRect.left < 100) {
          // If near left edge of screen
          newPosition = 'bottom-full left-0 mb-1';
        }
      }

      setCalculatedPosition(newPosition);
    }
  }, [isVisible, position]);

  return (
    <div 
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {isVisible && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap ${calculatedPosition} ${className}`}
          role="tooltip"
        >
          {content}
        </div>
      )}
      {children}
    </div>
  );
};

export default Tooltip;
